import { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';

export default function Contact() {
    useSEO(
        'Kontakt — PickOneGift',
        'Skontaktuj się z zespołem PickOneGift. Masz pytania, pomysły na współpracę B2B, a może coś nie działa? Napisz do nas!'
    );

    const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setFormState('submitting');
        
        // Mocking API call for demonstration
        setTimeout(() => {
            setFormState('success');
        }, 1200);
    };

    return (
        <div className="page">
            <Header />

            {/* Hero Section */}
            <div style={{
                textAlign: 'center',
                padding: '80px 24px 40px',
                background: 'linear-gradient(180deg, rgba(124, 92, 252, 0.08) 0%, transparent 100%)'
            }}>
                <h1 style={{ fontSize: '3rem', marginBottom: '16px' }}>
                    Porozmawiajmy o <span style={{ background: 'var(--gradient-primary)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Prezentach</span>
                </h1>
                <p className="text-muted" style={{ fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto' }}>
                    Niezależnie od tego, czy masz problem techniczny, szukasz partnerstwa B2B jako sklep, czy po prostu chcesz powiedzieć "cześć" – jesteśmy tu dla Ciebie.
                </p>
            </div>

            <div className="container" style={{ padding: '0 24px 80px', flex: 1 }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '40px',
                    maxWidth: '1000px',
                    margin: '0 auto'
                }}>
                    {/* Left Column - Contact Info */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                        <div className="card glass" style={{ borderLeft: '4px solid var(--color-primary)' }}>
                            <h3 style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '1.5rem' }}>💬</span> Wsparcie
                            </h3>
                            <p className="text-muted" style={{ marginBottom: '16px' }}>
                                Problem z listą? Nie wiesz jak dodać prezent? Nasz zespół chętnie pomoże.
                            </p>
                            <a href="mailto:support@pickonegift.net" style={{ fontWeight: 600 }}>support@pickonegift.net</a>
                        </div>

                        <div className="card glass" style={{ borderLeft: '4px solid var(--color-accent)' }}>
                            <h3 style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '1.5rem' }}>💼</span> Sklepy E-commerce
                            </h3>
                            <p className="text-muted" style={{ marginBottom: '16px' }}>
                                Chcesz dodać widget PickOneGift do swojego sklepu? Zapraszamy do działu B2B.
                            </p>
                            <a href="mailto:b2b@pickonegift.net" style={{ fontWeight: 600 }}>b2b@pickonegift.net</a>
                        </div>

                        <div className="card glass" style={{ borderLeft: '4px solid var(--color-success)' }}>
                            <h3 style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '1.5rem' }}>📍</span> Siedziba
                            </h3>
                            <p className="text-muted">
                                PickOneGift sp. z o.o.<br />
                                ul. Innowacyjna 42<br />
                                00-001 Warszawa, Polska
                            </p>
                        </div>
                    </div>

                    {/* Right Column - Contact Form */}
                    <div className="card glass" style={{ padding: '32px' }}>
                        <h2 style={{ marginBottom: '24px' }}>Napisz do nas</h2>
                        
                        {formState === 'success' ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                background: 'rgba(74, 222, 128, 0.1)',
                                borderRadius: '12px',
                                border: '1px solid rgba(74, 222, 128, 0.3)'
                            }}>
                                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✅</div>
                                <h3>Wiadomość wysłana!</h3>
                                <p className="text-muted" style={{ marginTop: '8px' }}>
                                    Dziękujemy za kontakt. Odpowiemy najszybciej jak to możliwe (zazwyczaj w ciągu 24h).
                                </p>
                                <button 
                                    className="btn btn-secondary" 
                                    style={{ marginTop: '24px' }}
                                    onClick={() => setFormState('idle')}
                                >
                                    Wyślij kolejną
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="name">Imię i nazwisko</label>
                                    <input 
                                        id="name"
                                        type="text" 
                                        className="form-input" 
                                        placeholder="Jan Kowalski" 
                                        required 
                                        disabled={formState === 'submitting'}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="email">Twój adres e-mail</label>
                                    <input 
                                        id="email"
                                        type="email" 
                                        className="form-input" 
                                        placeholder="jan@domena.pl" 
                                        required 
                                        disabled={formState === 'submitting'}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="topic">Temat (opcjonalnie)</label>
                                    <select 
                                        id="topic"
                                        className="form-select" 
                                        disabled={formState === 'submitting'}
                                    >
                                        <option value="general">Pytanie ogólne</option>
                                        <option value="support">Pomoc techniczna</option>
                                        <option value="b2b">Współpraca (Sklepy/Firmy)</option>
                                        <option value="feedback">Sugestia / Feedback</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="message">Treść wiadomości</label>
                                    <textarea 
                                        id="message"
                                        className="form-textarea" 
                                        placeholder="W czym możemy pomóc?" 
                                        rows={5}
                                        required
                                        disabled={formState === 'submitting'}
                                    ></textarea>
                                </div>
                                
                                <button 
                                    type="submit" 
                                    className="btn btn-primary" 
                                    style={{ marginTop: '8px' }}
                                    disabled={formState === 'submitting'}
                                >
                                    {formState === 'submitting' ? 'Wysyłanie...' : 'Wyślij wiadomość'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}
